import { useMemo } from 'react';

import { applyIdentityMask, applyMask } from '@src/common/masks';
import { useTranslate } from '@src/core/hooks';
import {
  buildBuyerProfileFields,
  BuyerDocumentKind,
  BuyerProfileFieldKind,
  BuyerProfileVariant,
  resolveCountryDisplay,
  type Buyer,
  type BuyerProfileField,
  type BuyerProfileVariant as Variant,
} from '@src/domains/customers';
import { buildOrderDetailsColumns } from '@src/features/orders/details/core/buildOrderDetailsColumns';
import type {
  OrderDetailsColumns,
  OrderDetailsFieldView,
  OrderPaymentDetails,
} from '@src/features/orders/details/types';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import type { DefaultMessagesKeys, Translate } from '@src/i18n/useTranslate';

const DOCUMENT_LABEL_KEYS: Record<string, DefaultMessagesKeys> = {
  [BuyerDocumentKind.Cpf]: 'checkout_order_details_cpf',
  [BuyerDocumentKind.Cnpj]: 'checkout_order_details_cnpj',
  [BuyerDocumentKind.NationalId]: 'checkout_order_details_national_document',
  [BuyerDocumentKind.Passport]: 'checkout_order_details_passport',
};

const isCompany = (variant: Variant) => variant === BuyerProfileVariant.DomesticCompany;
const isDomestic = (variant: Variant) =>
  variant === BuyerProfileVariant.DomesticCompany || variant === BuyerProfileVariant.DomesticIndividual;

const labelKeyFor = (field: BuyerProfileField, variant: Variant): DefaultMessagesKeys => {
  switch (field.kind) {
    case BuyerProfileFieldKind.Email:
      return 'checkout_order_details_email';
    case BuyerProfileFieldKind.AccountType:
      return 'checkout_order_details_account_type';
    case BuyerProfileFieldKind.FirstName:
      return isCompany(variant)
        ? 'checkout_order_details_representative_first_name'
        : 'checkout_order_details_first_name';
    case BuyerProfileFieldKind.LastName:
      return isCompany(variant)
        ? 'checkout_order_details_representative_last_name'
        : 'checkout_order_details_last_name';
    case BuyerProfileFieldKind.Country:
      return 'checkout_order_details_country';
    case BuyerProfileFieldKind.CorporateName:
      return 'checkout_order_details_corporate_name';
    case BuyerProfileFieldKind.Phone:
      return 'checkout_order_details_phone';
    case BuyerProfileFieldKind.ZipCode:
      return 'checkout_order_details_zip_code';
    default:
      return DOCUMENT_LABEL_KEYS[field.documentKind ?? BuyerDocumentKind.Cpf]!;
  }
};

const countryName = (alpha2: string, fallbackName: string, locale: string) => {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(alpha2) ?? fallbackName;
  } catch {
    return fallbackName;
  }
};

const toFieldView = (
  field: BuyerProfileField,
  variant: Variant,
  i18n: Translate['i18n'],
  locale: string,
): OrderDetailsFieldView => {
  const base = { key: field.kind, label: i18n(labelKeyFor(field, variant)) };

  switch (field.kind) {
    case BuyerProfileFieldKind.AccountType:
      return {
        ...base,
        value: i18n(
          isCompany(variant)
            ? 'checkout_order_details_account_type_company'
            : 'checkout_order_details_account_type_individual',
        ),
        adornment: { kind: 'account-type', accountType: isCompany(variant) ? 'company' : 'individual' },
      };

    case BuyerProfileFieldKind.Country: {
      const country = resolveCountryDisplay(field.countryCode);

      return {
        ...base,
        value: country ? countryName(country.alpha2, country.fallbackName, locale) : field.value,
        ...(country ? { adornment: { kind: 'flag' as const, code: country.alpha2 } } : {}),
      };
    }

    case BuyerProfileFieldKind.Document:
      return {
        ...base,
        value: isDomestic(variant) ? applyIdentityMask(field.value) : field.value,
      };

    // Both masks are Brazilian formats, so a foreign buyer keeps the raw value.
    case BuyerProfileFieldKind.Phone:
      return { ...base, value: isDomestic(variant) ? applyMask(field.value, 'brazilian_phone') : field.value };

    case BuyerProfileFieldKind.ZipCode:
      return { ...base, value: isDomestic(variant) ? applyMask(field.value, 'cep') : field.value };

    default:
      return { ...base, value: field.value };
  }
};

interface Params {
  buyer: Buyer;
  payment?: OrderPaymentDetails;
}

export function useOrderDetailsFields(params: Params): { columns: OrderDetailsColumns } {
  const { buyer, payment } = params;
  const { i18n, locale } = useTranslate();
  const { formatCurrencyInfo } = useCurrencyFormat();

  const columns = useMemo(() => {
    const { variant, fields } = buildBuyerProfileFields(buyer);
    const views = fields.map((field) => toFieldView(field, variant, i18n, locale));

    if (payment) {
      views.push({
        key: 'payment-method',
        label: i18n('checkout_order_details_payment_method'),
        value: payment.methodLabel,
        ...(payment.isPix
          ? { adornment: { kind: 'pix' as const } }
          : payment.brandCode
          ? { adornment: { kind: 'card-brand' as const, code: payment.brandCode } }
          : {}),
      });

      if (payment.showsInstallments && payment.installments && payment.installmentAmount) {
        views.push({
          key: 'installments',
          label: i18n('checkout_order_details_installments'),
          value: i18n('checkout_order_details_installments_value', {
            count: payment.installments,
            amount: formatCurrencyInfo(payment.installmentAmount),
          }),
        });
      }
    }

    return buildOrderDetailsColumns(views);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n and the formatter are stable per locale
  }, [buyer, payment, locale]);

  return { columns };
}

export default useOrderDetailsFields;
